import { NamesPurpose_SharedAb, t } from "@/global";
import { NameEditor } from "@/pages/DeviceList/DevicesTable";
import { addSharedAb, updateSharedAbProfile } from "@/services/api";
import { API } from "@/services/typings";
import { ModalForm, ProFormItem, ProFormText, ProFormTextArea } from "@ant-design/pro-form";
import { ActionType } from "@ant-design/pro-table";
import { useIntl, useModel } from "@umijs/max";
import { message, Modal, Typography, Form, Checkbox } from "antd";
import { useState, useEffect } from "react";

type CreateOrUpdateFormProps = {
  isCreate: boolean;
  visible: boolean;
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  actionRef: React.MutableRefObject<ActionType | undefined>;
  initialValues: API.SharedAbProfile | undefined;
}

const CreateOrUpdateForm: React.FC<CreateOrUpdateFormProps> = (props) => {
  const { initialState } = useModel('@@initialState');
  const is_admin = initialState?.user?.is_admin;
  const canTransfer = !props.isCreate && (is_admin || props.initialValues?.owner === initialState?.user?.name);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferModalData, setTransferModalData] = useState<Partial<API.SharedAbProfile> | null>(null);
  const intl = useIntl();
  const [removePassword, setRemovePassword] = useState(false);

  // Reset removePassword state when modal is opened
  useEffect(() => {
    if (props.visible) {
      setRemovePassword(false);
    }
  }, [props.visible]);

  // plain for existing 'Owner' translation
  const plain = (text: string, defaultMessage = '') => t(`pages.AddressBook.${text}`, true, defaultMessage || text, intl) as string;


  const handleCreate = async (data: any) => {
    const hide = message.loading(t('Adding'));
    try {
      const PersonalAddressBookName = "My address book";
      const LegacyAddressBookName = "Legacy address book";
      if (data.name === PersonalAddressBookName) {
        message.success(t(`Name can not be ${PersonalAddressBookName}`));
        return;
      }
      if (data.name === LegacyAddressBookName) {
        message.success(t(`Name can not be ${LegacyAddressBookName}`));
        return;
      }
      const { password, ...restData } = data;
      const payload = password
        ? { ...restData, info: { password } }
        : restData;
      await addSharedAb({ data: payload });
      hide();
      message.success(t('Added successfully'));
      return true;
    } catch (error) {
      hide();
      message.error(
        typeof error == 'string' ? (error as string) : t('Adding failed, please try again!'),
      );
      return false;
    }
  };

  const handleUpdate = async (data: any) => {
    let hide = message.loading(t('Updating'));
    try {
      data['guid'] = props.initialValues?.guid;

      const changedData: Partial<API.SharedAbProfile> = {
        guid: data.guid
      };

      let transfer = false;

      if ((data.name || '') !== (props.initialValues?.name || '')) {
        changedData.name = data.name;
      }
      if (canTransfer && (data.owner || '') !== (props.initialValues?.owner || '')) {
        changedData.owner = data.owner;
        transfer = true;
      }
      if ((data.note || '') !== (props.initialValues?.note || '')) {
        changedData.note = data.note;
      }
      let password = data.password;
      if (removePassword) {
        changedData.info = {};
      } else {
        if (!password) {
          // keep unchanged
          changedData.info = undefined;
        } else {
          changedData.info = {
            password,
          };
        }
      }
      if (transfer) {
        hide();
        setTransferModalData(changedData);
        setTransferModalVisible(true);
        props.setModalVisible(false);
        return false;
      } else {
        await updateSharedAbProfile({ data: changedData });
        hide();
        message.success(t('Update is successful'));
      }

      return true;
    } catch (error) {
      hide();
      message.error(
        typeof error == 'string' ? (error as string) : t('Update failed, please try again!'),
      );
      return false;
    }
  };

  const handleTransferConfirm = async () => {
    if (!props.initialValues?.guid || !transferModalData) {
      return;
    }
    let hide = message.loading(t('Updating'));
    try {
      await updateSharedAbProfile({ data: transferModalData });
      setTransferModalVisible(false);
      setTransferModalData(null);
      hide();
      message.success(t('Update is successful'));
      if (props.actionRef.current) {
        props.actionRef.current.reload();
      }
      props.setModalVisible(false)
      return true;
    } catch (error) {
      hide();
      message.error(
        typeof error == 'string' ? (error as string) : t('Update failed, please try again!'),
      ); return false;
    }
  };

  return (
    <Form
      layout="vertical"
      initialValues={props.initialValues}
      onFinish={handleUpdate}
    >
      <Modal
        title={<span><span>{t('transfer_address_book')}</span><span> </span><Typography.Text mark>{props.initialValues?.name}</Typography.Text></span>}
        open={transferModalVisible}
        onOk={handleTransferConfirm}
        onCancel={() => {
          setTransferModalVisible(false);
          setTransferModalData(null);
        }}
      >
        <div>
          <span>
            {t('transfer_address_book_confirm')}
          </span>
        </div>
        <div>
          {plain('Owner')}{': '} <Typography.Text mark>{props.initialValues?.owner}</Typography.Text> {' -> '} <Typography.Text mark>{transferModalData?.owner}</Typography.Text>
        </div>
      </Modal>
      <ModalForm
        title={t(props.isCreate ? 'Create' : 'Edit')}
        width="400px"
        open={props.visible}
        initialValues={props.initialValues}
        onOpenChange={props.setModalVisible}
        onFinish={async (values) => {
          const success = await (props.isCreate ? handleCreate : handleUpdate)(values);
          if (success) {
            if (props.actionRef.current) {
              props.actionRef.current.reload();
            }
            props.setModalVisible(false);
          }
        }}
        modalProps={{
          maskClosable: false,
          destroyOnClose: true,
          onCancel: () => {
            props.setModalVisible(false);
          },
        }}
      >
        <ProFormText
          fieldProps={{ autoComplete: 'off' }}
          rules={[
            {
              required: true,
              whitespace: true,
            },
            {
              min: 4,
              max: 30,
            },
          ]}
          width="md"
          name="name"
          label={t('Name', true)}
        />
        {canTransfer && <NameEditor table={"user"} field={"owner"} label={"Owner"} required purpose={NamesPurpose_SharedAb} />}
        {!(!props.isCreate && removePassword) && <ProFormText.Password
          fieldProps={{ autoComplete: 'new-password', maxLength: 128, type: 'password' }}
          rules={[
            {
              required: false,
              whitespace: false,
            }
          ]}
          width="md"
          name="password"
          label={t('default_shared_password', true)}
          tooltip={t('default_shared_password_tip')}
        />}
        {!props.isCreate && <ProFormItem>
          <Checkbox value={removePassword} onChange={(e) => setRemovePassword(e.target.checked)}>
            {t('Remove password', true)}
          </Checkbox>
        </ProFormItem>}
        <ProFormTextArea
          fieldProps={{ autoComplete: 'off' }}
          rules={[
            {
              max: 300,
            },
          ]}
          width="md"
          name="note"
          label={t('Note', true)}
        />
      </ModalForm>
    </Form>
  )
}

export default CreateOrUpdateForm;

